import { useEffect, useState } from 'react';
import type { ListedObject, UserId } from '../types';
import { copyToClipboard, formatBytes, formatRelative } from '../lib/format';
import { formatPixelsAndRatio, readImageDimensions } from '../lib/dimensions';
import { markdownEmbed } from '../lib/files';
import { Spinner } from './Spinner';
import { FileIcon } from './FileIcon';

interface LightboxProps {
  object: ListedObject;
  user: UserId;
  onClose: () => void;
  onDelete: (key: string) => Promise<void>;
  onCopied: (label: string) => void;
}

function splitUserKey(key: string, user: UserId): { folder: string; filename: string } {
  const userPrefix = `${user}/`;
  const rel = key.startsWith(userPrefix) ? key.slice(userPrefix.length) : key;
  const lastSlash = rel.lastIndexOf('/');
  if (lastSlash < 0) return { folder: '', filename: rel };
  return { folder: rel.slice(0, lastSlash), filename: rel.slice(lastSlash + 1) };
}

export function Lightbox({ object, user, onClose, onDelete, onCopied }: LightboxProps) {
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  const { folder, filename } = splitUserKey(object.key, user);
  const markdown = markdownEmbed(filename, object.url, object.kind);
  const uploadedAtMs = Date.parse(object.uploaded);
  const isImage = object.kind === 'image';
  const ratioText = dimensions
    ? formatPixelsAndRatio(dimensions.width, dimensions.height)
    : null;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleCopy = async (label: 'URL' | 'Markdown', payload: string) => {
    const ok = await copyToClipboard(payload);
    if (ok) {
      setCopied(label);
      onCopied(label);
      window.setTimeout(() => setCopied((v) => (v === label ? null : v)), 1400);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      window.setTimeout(() => setConfirmingDelete(false), 4000);
      return;
    }
    setDeleting(true);
    try {
      await onDelete(object.key);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={filename}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="surface flex max-h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 border-b border-app-border px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium" title={filename}>{filename}</p>
            <p className="truncate text-xs text-muted">
              {folder ? <>folder: <code className="font-mono">{folder}</code> · </> : null}
              {formatBytes(object.size)}
              {ratioText ? ` · ${ratioText}` : ''}
              {' · '}
              {formatRelative(uploadedAtMs)}
              {object.contentType ? ` · ${object.contentType}` : ''}
            </p>
          </div>
          <button type="button" className="btn btn-ghost text-xs" onClick={onClose} aria-label="Close">
            Close (Esc)
          </button>
        </header>

        <div className="flex min-h-0 flex-1 items-center justify-center bg-black/40 p-4">
          {isImage ? (
            <img
              src={object.url}
              alt={filename}
              className="max-h-[60dvh] max-w-full object-contain"
              onLoad={(e) => setDimensions(readImageDimensions(e.currentTarget))}
            />
          ) : (
            <div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
              <FileIcon kind={object.kind} filename={filename} size={120} className="h-32 w-32" />
              <div>
                <p className="text-sm">
                  <span className="text-app">{filename}</span>
                </p>
                <p className="text-xs text-muted">
                  Inline preview isn't available for this type. Open the raw URL or download below.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3 border-t border-app-border p-4">
          <div>
            <p className="label mb-1">Public URL</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 break-all rounded-md bg-app-surface-2 px-3 py-2 font-mono text-xs">
                {object.url}
              </code>
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={() => handleCopy('URL', object.url)}
              >
                {copied === 'URL' ? 'Copied!' : 'Copy URL'}
              </button>
            </div>
          </div>
          <div>
            <p className="label mb-1">Markdown</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 break-all rounded-md bg-app-surface-2 px-3 py-2 font-mono text-xs">
                {markdown}
              </code>
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={() => handleCopy('Markdown', markdown)}
              >
                {copied === 'Markdown' ? 'Copied!' : 'Copy Markdown'}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <a
              href={object.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost text-xs"
            >
              Open raw
            </a>
            <a
              href={object.url}
              download={filename}
              className="btn btn-ghost text-xs"
            >
              Download
            </a>
            <button
              type="button"
              className="btn btn-danger text-xs"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? <Spinner size={12} /> : null}
              <span>
                {deleting
                  ? 'Deleting…'
                  : confirmingDelete
                    ? 'Click again to confirm delete'
                    : 'Delete from R2'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
